import React, { useEffect, useState, useCallback, useRef } from "react";
import classNames from "classnames";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import "../SpaceContent.scss";
import { SimpleInput } from "../../CustomInput/SimpleInput";
import { ReactComponent as Facebook } from "../../../assets/icon/facebook-f.svg";
import { ReactComponent as Twitter } from "../../../assets/icon/twitter.svg";
import { ReactComponent as Linkedin } from "../../../assets/icon/linkedin-in.svg";
import { ReactComponent as Pinterest } from "../../../assets/icon/pinterest-p.svg";
import { ReactComponent as Mail } from "../../../assets/icon/mail.svg";
import { ReactComponent as Back } from "../../../assets/icon/arrow-left.svg";
import { 
  getShareParams, 
  getSweepId, 
  getSweepNumber,
  setShareParams,
  setSweepId,
  setSweepNumber 
} from "../../../helpers/rotationStore";

const SpaceShareBtn = styled("button", {
  shouldForwardProp: (prop) => prop !== "color",
})(({ color }) => ({
  "&:after": {
    background: color,
  },
}));

const Share = ({ space, matterport, back }) => {
  const value = window.location.href;
  const [shareState, setShareState] = useState({
    website: false,
    embed: false,
    matterport: false,
    matterportValue: "",
  });
  const [menuWidth, setMenuWidth] = useState(null);
  const color = useSelector((state) => state.profile.color);

  // Crear el deep link como una función reutilizable con useCallback
  const generateDeepLink = useCallback(async (mp) => {
    if (!mp) return null;

    try {
      await mp.Link.setShareLinkPolicy(
        mp.Link.CreationPolicy.REFERRER
      );

      let link = await mp.Link.createDeepLink();
      let getParams = new URL(link);
      const searchParams = new URLSearchParams(getParams.search);

      // Obtener los parámetros existentes
      let sm = searchParams.get("sm");
      let sp = searchParams.get("sp");
      let sf = searchParams.get("sf");

      // Obtener el número de sweep y parámetros de rotación directamente
      const sweepNumber = getSweepNumber();
      const sweepId = getSweepId();
      const { sr } = getShareParams();

      console.log("Deep Link Debug - sweepNumber:", sweepNumber, "sweepId:", sweepId, "sr:", sr);

      // Crear los parámetros finales
      const createParams = () => {
        let paramsString = "";
        if (sm !== null) paramsString += `&sm=${sm}`;
        if (sr) {
          paramsString += `&sr=${sr}`;
        } else if (searchParams.get("sr") !== null) {
          paramsString += `&sr=${searchParams.get("sr")}`;
        }
        if (sp !== null) paramsString += `&sp=${sp}`;
        if (sf !== null) paramsString += `&sf=${sf}`;
        if (sweepNumber !== null) paramsString += `&ss=${sweepNumber}`;
        return paramsString;
      };

      let params = createParams();
      let searchSpace = window.location.search.split("&")[0];
      let createDeep = window.location.origin + window.location.pathname + searchSpace;

      // Remove all after '?' in createDeep (In case the current URL already has parameters), but keep the first '?'
      if (createDeep.includes("?")) {
        createDeep = createDeep.split("?")[0] + "?";
      }
      // Add the new parameters to the deep link
      if (params) {
        params = params.replace(/^\?/, "&");
      }

      if (!searchSpace) {
        params = params.replace(/^&/, "?");
        createDeep += params;
      } else {
        createDeep += params;
      }

      console.log("Generated Deep Link:", createDeep);
      return createDeep;
    } catch (error) {
      console.error("Error generating deep link:", error);
      return window.location.href;
    }
  }, []);

  // Función para actualizar el deep link
  const updateDeepLink = useCallback(async () => {
    if (!matterport) return;

    const now = Date.now();
    // Limitar la frecuencia de actualizaciones a una vez cada segundo
    if (now - shareState.lastGeneratedTime < 1000) return;

    try {
      const deepLink = await generateDeepLink(matterport);
      if (deepLink) {
        setShareState(prev => ({
          ...prev,
          matterportValue: deepLink,
          lastGeneratedTime: now
        }));
      }
    } catch (error) {
      console.error("Error updating deep link:", error);
    }
  }, [matterport, generateDeepLink, shareState.lastGeneratedTime]);

  // Función para forzar una actualización directa desde la API de Matterport
  const forcePositionUpdate = useCallback(async () => {
    if (!matterport) return;
    
    try {
      // Obtener información de posición directamente
      const pose = await matterport.Camera.getPose();
      if (!pose) return null;
      
      // Guardar la rotación
      const yaw = parseFloat(pose.rotation.y.toFixed(2));
      const pitch = parseFloat(pose.rotation.x.toFixed(2));
      const sr = `${yaw},${pitch}`;
      setShareParams(sr);
      
      // Obtener información del sweep
      const modelData = await matterport.Model.getData();
      if (!modelData || !modelData.sweeps) return null;
      
      const sweepIndex = modelData.sweeps.findIndex(s => s.sid === pose.sweep);
      if (sweepIndex >= 0) {
        const sweepNumber = sweepIndex + 1;
        setSweepNumber(sweepNumber);
        setSweepId(pose.sweep);
        console.log(`Position force-updated - Sweep #${sweepNumber}, SID: ${pose.sweep}, Rotation: ${sr}`);
      }
      
      // Generar y devolver el deep link
      return await generateDeepLink(matterport);
    } catch (error) {
      console.error("Error updating position:", error);
      return null;
    }
  }, [matterport]);

  useEffect(() => {
    if (!matterport) return;
    
    const initializeDeepLink = async () => {
      console.log("Initializing deep link...");
      
      // Primero intentar con los datos actuales
      const existingDeepLink = await generateDeepLink(matterport);
      
      if (existingDeepLink) {
        console.log("Generated deep link with existing data:", existingDeepLink);
        setShareState(prev => ({ ...prev, matterportValue: existingDeepLink }));
      } else {
        // Si no hay datos o el deep link no se generó, forzar una actualización
        console.log("No existing data, forcing position update...");
        const forcedDeepLink = await forcePositionUpdate();
        
        if (forcedDeepLink) {
          console.log("Generated deep link after force update:", forcedDeepLink);
          setShareState(prev => ({ ...prev, matterportValue: forcedDeepLink }));
        } else {
          console.warn("Could not generate deep link");
        }
      }
    };
    
    // Inicializar inmediatamente
    initializeDeepLink();
    
    // Configurar un intervalo para intentar actualizar si no tenemos un deep link
    const intervalId = setInterval(() => {
      if (!shareState.matterportValue) {
        console.log("Still no deep link, retrying...");
        forcePositionUpdate().then(link => {
          if (link) {
            console.log("Finally generated deep link:", link);
            setShareState(prev => ({ ...prev, matterportValue: link }));
          }
        });
      }
    }, 2000); // Intentar cada 2 segundos si aún no hay deep link
    
    return () => clearInterval(intervalId);
  }, [matterport, forcePositionUpdate, generateDeepLink, shareState.matterportValue]);

  useEffect(() => {
    const updateMenuWidth = () => {
      const menuElement = document.querySelector('.main-nav__visible');
      if (menuElement) {
        const width = menuElement.offsetWidth;
        document.documentElement.style.setProperty('--menu-width', `${width}px`);
      }
    };

    const resizeObserver = new ResizeObserver(updateMenuWidth);
    const menuElement = document.querySelector('.main-nav__visible');

    if (menuElement) {
      resizeObserver.observe(menuElement);
      updateMenuWidth(); // Actualización inicial
    }

    return () => {
      if (menuElement) {
        resizeObserver.unobserve(menuElement);
      }
    };
  }, []);

  useEffect(() => {
    // Forzar recálculo cuando cambie el idioma
    const menuElement = document.querySelector('.main-nav__visible');
    if (menuElement) {
      const width = menuElement.offsetWidth;
      document.documentElement.style.setProperty('--menu-width', `${width}px`);
    }
  }, [space?.language]); // Dependencia del idioma

// Función para manejar la copia al portapapeles
const handleCopyClick = async (e, type) => {
  e.preventDefault();
    
    let valueToCopy;
    
    switch (type) {
      case "website":
        valueToCopy = window.location.href;
        break;
      case "embed":
        valueToCopy = `<iframe src=${space?.url} name="myIFrame" frameborder='0' allowfullscreen allow='xr-spatial-tracking' width='853' height='480'></iframe>`;
        break;
      case "matterport":
        // Siempre forzar una actualización antes de copiar el deep link
        const freshDeepLink = await forcePositionUpdate();
        valueToCopy = freshDeepLink || shareState.matterportValue || window.location.href;
        if (freshDeepLink && freshDeepLink !== shareState.matterportValue) {
          setShareState(prev => ({ ...prev, matterportValue: freshDeepLink }));
        }
        break;
      default:
        valueToCopy = "";
    }
  
  // Copiar al portapapeles
  try {
    if ("clipboard" in navigator) {
      await navigator.clipboard.writeText(valueToCopy);
    } else {
      document.execCommand("copy", true, valueToCopy);
    }
    
    // Actualizar estado para mostrar feedback
    setShareState(prev => ({ ...prev, [type]: true }));
    setTimeout(() => {
      setShareState(prev => ({ ...prev, [type]: false }));
    }, 1500);
  } catch (err) {
    console.error("Error copying to clipboard:", err);
  }
};

  return (
    <div
      className={classNames({
        "space-share": true,
        "space-share--position_tile": (
          !space?.tileSettings?.showTileMenu && 
          space?.visibility?.conferenceCallVisibility && 
          space?.visibility?.poisVisibility && 
          space !== null
        ),
        "space-share--position_conference": (
          !space?.visibility?.conferenceCallVisibility &&
          space?.tileSettings?.showTileMenu && 
          space?.visibility?.poisVisibility && 
          space !== null
        ),
        "space-share--all": (
          !space?.visibility?.conferenceCallVisibility &&
          !space?.tileSettings?.showTileMenu &&
          space?.visibility?.poisVisibility && 
          space !== null
        ),
        "space-share--poi": (
          !space?.visibility?.poisVisibility &&
          space?.tileSettings?.showTileMenu && 
          space?.visibility?.conferenceCallVisibility && 
          space !== null
        ),
        "space-share--poi_tiles": (
          !space?.visibility?.poisVisibility &&
          !space?.tileSettings?.showTileMenu && 
          space?.visibility?.conferenceCallVisibility && 
          space !== null
        ),
        "space-share--all_hidden": (
          !space?.visibility?.conferenceCallVisibility &&
          !space?.tileSettings?.showTileMenu &&
          !space?.visibility?.poisVisibility &&
          space !== null
        )
      })}
    >
      <div onClick={back} className="profile-back">
        <Back />
        <p>Back to Menu</p>
      </div>
      <div className="space-share__social">
        <div
          onClick={() => {
            const windowFeatures = "left=100,top=100,width=520,height=520";
            const handle = window.open(
              `http://www.facebook.com/sharer/sharer.php?u=${value}&title=title`,
              "facebookWindow",
              windowFeatures
            );
          }}
          className="space-share__icon"
        >
          <Facebook />
        </div>
        <div
          onClick={() => {
            const windowFeatures = "left=100,top=100,width=520,height=520";
            const handle = window.open(
              `https://twitter.com/intent/tweet?original_referer=${value}&text=space&url=${value}`,
              "facebookWindow",
              windowFeatures
            );
          }}
          className="space-share__icon"
        >
          <Twitter />
        </div>
        <div
          onClick={() => {
            const windowFeatures = "left=100,top=100,width=520,height=520";
            const handle = window.open(
              `http://www.linkedin.com/shareArticle?mini=true&url=${value}`,
              "facebookWindow",
              windowFeatures
            );
          }}
          className="space-share__icon"
        >
          <Linkedin />
        </div>
        <div
          onClick={() => {
            const windowFeatures = "left=100,top=100,width=520,height=520";
            const handle = window.open(
              `http://pinterest.com/pin/create/bookmarklet/?media=${value}&url=${value}&is_video=false&description=[TITLE]`,
              "facebookWindow",
              windowFeatures
            );
          }}
          className="space-share__icon"
        >
          <Pinterest />
        </div>
        <a href="mailto:webmaster@example.com" className="space-share__icon">
          <Mail />
        </a>
      </div>
      <div className="space-share__wrapper">
        <div className="space-share__hero">
          <p className="space-share-title">Link</p>
          <div className="space-share__main">
            <SimpleInput value={value} disabled={true} />
            <SpaceShareBtn
              onClick={(e) => handleCopyClick(e, "website")}
              className="space-share__btn"
              color={color}
            >
              {shareState.website ? "Copied!" : "Copy"}
            </SpaceShareBtn>
          </div>
        </div>
        <div className="space-share__hero">
          <p className="space-share-title">Deep link</p>
          <div className="space-share__main">
            <SimpleInput value={shareState.matterportValue} disabled={true} />
            <SpaceShareBtn
              onClick={(e) => handleCopyClick(e, "matterport")}
              className="space-share__btn"
              color={color}
            >
              {shareState.matterport ? "Copied!" : "Copy"}
            </SpaceShareBtn>
          </div>
        </div>
        <div className="space-share__hero">
          <p className="space-share-title">Embed</p>
          <div className="space-share__main">
            <SimpleInput
              value={`<iframe src=${space?.url} name="myIFrame" scrolling="auto" width="50%" height="50%" ></iframe>`}
              disabled={true}
            />
            <SpaceShareBtn
              onClick={(e) => handleCopyClick(e, "embed")}
              className="space-share__btn"
              color={color}
            >
              {shareState.embed ? "Copied!" : "Copy"}
            </SpaceShareBtn>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Share;
